import React from 'react';
import { Card, Row, Col, Divider, Table, Tag, Typography, Timeline, Alert } from 'antd';
import {
  TrophyOutlined,
  StarOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  TeamOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import SimpleAdminLayout from '../components/SimpleAdminLayout';
import './RankingSystem.css';

const { Title, Paragraph, Text } = Typography;

const RankingSystem = () => {

  const scoringColumns = [
    {
      title: 'Kriteria',
      dataIndex: 'criteria',
      key: 'criteria',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Poin',
      dataIndex: 'score',
      key: 'score',
      render: (score) => <Tag color="blue">{score}</Tag>
    },
    {
      title: 'Keterangan',
      dataIndex: 'description',
      key: 'description'
    }
  ];

  const scoringData = [
    {
      key: '1',
      criteria: 'Video Disetujui',
      score: '+1 poin',
      description: 'Setiap video yang diverifikasi dan disetujui'
    },
    {
      key: '2',
      criteria: 'Sertifikasi AI Disetujui',
      score: '+1 poin',
      description: 'Setiap sertifikasi AI yang diverifikasi dan disetujui'
    },
    {
      key: '3',
      criteria: 'Bonus Campaign',
      score: 'Variabel',
      description: 'Poin bonus tambahan sesuai konfigurasi campaign (jika ada)'
    }
  ];

  const participationColumns = [
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text, record) => (
        <div>
          <span style={{ fontSize: 18, marginRight: 8 }}>{record.icon}</span>
          <Text strong>{text}</Text>
        </div>
      )
    },
    {
      title: 'Formula Partisipasi',
      dataIndex: 'formula',
      key: 'formula',
      render: (formula) => (
        <code style={{
          background: '#f0f2f5',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12
        }}>
          {formula}
        </code>
      )
    },
    {
      title: 'Contoh',
      dataIndex: 'example',
      key: 'example'
    }
  ];

  const participationData = [
    {
      key: '1',
      role: 'Dosen',
      icon: '👨‍🏫',
      formula: '(Jumlah video dosen / Total dosen) × 100%',
      example: '50 video / 20 dosen = 250%'
    },
    {
      key: '2',
      role: 'Mahasiswa',
      icon: '👨‍🎓',
      formula: '(Jumlah video mahasiswa / Total mahasiswa) × 100%',
      example: '100 video / 200 mahasiswa = 50%'
    },
    {
      key: '3',
      role: 'Tenaga Pendidik',
      icon: '👷',
      formula: '(Jumlah video tendik / Total tendik) × 100%',
      example: '30 video / 10 tendik = 300%'
    }
  ];

  return (
    <SimpleAdminLayout>
      <div className="ranking-system-container">
        <Card className="admin-panel-card">
          <div className="admin-panel-header">
            <Title level={2} style={{ margin: 0 }}>
              <TrophyOutlined style={{ color: '#ffd700', marginRight: 8 }} />
              Skema Perankingan
            </Title>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              Memahami bagaimana sistem menghitung poin dan meranking kontributor
            </p>
          </div>

          <div className="ranking-system-content">
        {/* Overview */}
        <Card className="ranking-card">
          <Title level={3}>
            <StarOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
            Gambaran Umum
          </Title>
          <Paragraph>
            Sistem perankingan gamifikasi ini dirancang untuk mendorong partisipasi aktif dari seluruh sivitas akademika
            (dosen, mahasiswa, dan tenaga pendidik) dalam membuat dan membagikan konten edukatif yang berkualitas.
          </Paragraph>
          <Alert
            message="Prinsip Fair Play"
            description="Semua perhitungan poin dan ranking dilakukan secara otomatis dan transparan oleh sistem. Setiap konten harus melalui proses verifikasi sebelum mendapat poin."
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Card>

        {/* Scoring System */}
        <Card className="ranking-card">
          <Title level={3}>
            <CalculatorOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
            Sistem Penilaian (Scoring)
          </Title>
          <Paragraph>
            Setiap konten yang diverifikasi dan disetujui akan mendapatkan poin berdasarkan kriteria berikut:
          </Paragraph>
          <Table
            columns={scoringColumns}
            dataSource={scoringData}
            pagination={false}
            bordered
          />
          <Divider />
          <Alert
            message="Catatan Penting"
            description="Konten yang ditolak tidak akan mendapatkan poin. Pastikan konten sesuai dengan panduan sebelum disubmit."
            type="warning"
            showIcon
          />
        </Card>

        {/* Ranking Calculation */}
        <Card className="ranking-card">
          <Title level={3}>
            <TrophyOutlined style={{ color: '#ffd700', marginRight: 8 }} />
            Perhitungan Ranking
          </Title>

          <Title level={4}>1. Ranking Individual (Dosen, Mahasiswa, Tendik)</Title>
          <Paragraph>
            Ranking individual diurutkan berdasarkan <Text strong>Total Poin</Text> yang dikumpulkan dari video dan sertifikasi yang diverifikasi.
          </Paragraph>
          <Timeline
            items={[
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 1:</Text> Sistem menghitung poin video yang diverifikasi (+1 per video)</Text>
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 2:</Text> Sistem menghitung poin sertifikasi yang diverifikasi (+1 per sertifikasi)</Text>
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 3:</Text> Total poin = poin video + poin sertifikasi</Text>
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 4:</Text> Individu diurutkan berdasarkan total poin tertinggi</Text>
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 5:</Text> Jika total poin sama, urutan ditentukan oleh total video, lalu total sertifikasi</Text>
              }
            ]}
          />
          <div style={{
            background: '#e6f7ff',
            padding: 16,
            borderRadius: 8,
            marginTop: 16,
            border: '1px solid #91d5ff'
          }}>
            <Text strong>Formula:</Text>
            <div style={{ marginTop: 8 }}>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block' }}>
                Total Poin = (Jumlah Video Verified × 1) + (Jumlah Sertifikasi Verified × 1)
              </code>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block', marginTop: 8 }}>
                Rank = Urutan berdasarkan: Total Poin DESC → Total Video DESC → Total Sertifikasi DESC
              </code>
            </div>
            <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
              <Text strong>Contoh:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Dosen A: 10 video + 3 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>13 poin</Text></li>
                <li>Dosen B: 8 video + 5 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>13 poin</Text> (ranking sama dengan A, tapi karena video lebih sedikit, rank lebih rendah)</li>
                <li>Dosen C: 15 video + 0 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>15 poin</Text> (ranking #1)</li>
              </ul>
            </Paragraph>
          </div>

          <Divider />

          <Title level={4}>2. Ranking Program Studi</Title>
          <Paragraph>
            Ranking program studi diurutkan berdasarkan <Text strong>Total Poin</Text> yang dikumpulkan oleh seluruh anggota program studi (dosen, mahasiswa, dan tendik) dari video dan sertifikasi yang diverifikasi.
          </Paragraph>
          <Timeline
            items={[
              {
                color: 'green',
                children: <Text><Text strong>Langkah 1:</Text> Sistem menghitung total poin setiap anggota (video + sertifikasi)</Text>
              },
              {
                color: 'green',
                children: <Text><Text strong>Langkah 2:</Text> Sistem menjumlahkan total poin dari semua anggota prodi (dosen + mahasiswa + tendik)</Text>
              },
              {
                color: 'green',
                children: <Text><Text strong>Langkah 3:</Text> Program studi diurutkan dari total poin tertinggi ke terendah</Text>
              },
              {
                color: 'green',
                children: <Text><Text strong>Langkah 4:</Text> Rank #1 = program studi dengan total poin tertinggi</Text>
              }
            ]}
          />
          <div style={{
            background: '#f6ffed',
            padding: 16,
            borderRadius: 8,
            marginTop: 16,
            border: '1px solid #b7eb8f'
          }}>
            <Text strong>Formula:</Text>
            <div style={{ marginTop: 8 }}>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block' }}>
                Poin Individual = (Video Verified × 1) + (Sertifikasi Verified × 1)
              </code>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block', marginTop: 8 }}>
                Total Poin Prodi = Σ(Poin Dosen) + Σ(Poin Mahasiswa) + Σ(Poin Tendik)
              </code>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block', marginTop: 8 }}>
                Rank = Position berdasarkan Total Poin Prodi (descending)
              </code>
            </div>
            <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
              <Text strong>Contoh Perhitungan - Teknik Informatika:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Dosen: 50 video + 10 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>60 poin</Text></li>
                <li>Mahasiswa: 200 video + 30 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>230 poin</Text></li>
                <li>Tendik: 20 video + 5 sertifikasi = <Text strong style={{ color: '#14B8A6' }}>25 poin</Text></li>
                <li>Total Poin Prodi: 60 + 230 + 25 = <Text strong style={{ color: '#722ed1' }}>315 poin</Text></li>
              </ul>
            </Paragraph>
          </div>
        </Card>

        {/* Participation Calculation */}
        <Card className="ranking-card">
          <Title level={3}>
            <RiseOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            Perhitungan Tingkat Partisipasi
          </Title>
          <Paragraph>
            Tingkat partisipasi mengukur seberapa aktif anggota program studi dalam berkontribusi membuat konten.
            Partisipasi dihitung per role untuk memberikan insight yang lebih detail.
          </Paragraph>

          <Title level={4}>Formula Partisipasi per Role</Title>
          <Table
            columns={participationColumns}
            dataSource={participationData}
            pagination={false}
            bordered
          />

          <Divider />

          <Title level={4}>Partisipasi Tingkat Program Studi</Title>
          <Paragraph>
            Partisipasi program studi dihitung berdasarkan <Text strong>total kontribusi (video)</Text> dari semua role dibandingkan dengan total anggota semua role.
          </Paragraph>

          <div style={{
            background: '#f9f0ff',
            padding: 16,
            borderRadius: 8,
            marginTop: 16,
            border: '1px solid #d3adf7'
          }}>
            <Text strong>Formula:</Text>
            <div style={{ marginTop: 8 }}>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block' }}>
                Partisipasi Prodi = (Total Kontribusi Semua Role / Total Anggota Semua Role) × 100%
              </code>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block', marginTop: 8 }}>
                Total Kontribusi = Video Dosen + Video Mahasiswa + Video Tendik
              </code>
              <code style={{ background: 'white', padding: 8, borderRadius: 4, display: 'block', marginTop: 8 }}>
                Total Anggota = Jumlah Dosen + Jumlah Mahasiswa + Jumlah Tendik
              </code>
            </div>
            <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
              <Text strong>Contoh Perhitungan - Teknik Informatika:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Anggota: 80 dosen + 1076 mahasiswa + 15 tendik = 1171 total anggota</li>
                <li>Kontribusi: 50 video dosen + 200 video mahasiswa + 20 video tendik = 270 total video</li>
                <li>Partisipasi: (270 / 1171) × 100% = <Text strong style={{ color: '#722ed1' }}>23.06%</Text></li>
              </ul>
            </Paragraph>
          </div>

          <Alert
            message="Perbedaan Partisipasi Per Role vs Partisipasi Prodi"
            description={
              <div>
                <p><Text strong>Partisipasi Per Role:</Text> Mengukur seberapa aktif role tertentu berkontribusi (bisa melebihi 100% jika rata-rata anggota membuat lebih dari 1 konten)</p>
                <p><Text strong>Partisipasi Prodi:</Text> Mengukur kontribusi keseluruhan program studi dengan mempertimbangkan semua role secara proporsional</p>
                <p style={{ marginBottom: 0 }}>Contoh: Jika dosen sangat aktif (250% partisipasi) tetapi mahasiswa kurang aktif (18% partisipasi), partisipasi prodi akan mencerminkan kombinasi keduanya dengan mempertimbangkan jumlah anggota masing-masing role.</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>

        {/* Ranking Display */}
        <Card className="ranking-card">
          <Title level={3}>
            <TeamOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
            Tampilan Ranking
          </Title>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Title level={4}>Badge Ranking</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Tag color="gold" icon={<TrophyOutlined />} style={{ fontSize: 16, padding: '8px 16px' }}>
                    #1 Rank 1
                  </Tag>
                  <Text>Juara 1 (Gold)</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Tag color="silver" icon={<TrophyOutlined />} style={{ fontSize: 16, padding: '8px 16px' }}>
                    #2 Rank 2
                  </Tag>
                  <Text>Juara 2 (Silver)</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Tag color="bronze" icon={<TrophyOutlined />} style={{ fontSize: 16, padding: '8px 16px', color: 'black' }}>
                    #3 Rank 3
                  </Tag>
                  <Text>Juara 3 (Bronze)</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Tag color="blue" icon={<StarOutlined />} style={{ fontSize: 16, padding: '8px 16px' }}>
                    #4+ Rank 4+
                  </Tag>
                  <Text>Lainnya (Blue)</Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Title level={4}>Akses Leaderboard</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Text>
                  <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
                  Leaderboard dapat diakses oleh siapa saja
                </Text>
                <Text>
                  <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
                  Data diupdate secara real-time
                </Text>
                <Text>
                  <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
                  Fitur filter berdasarkan campaign
                </Text>
                <Text>
                  <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
                  Detail kontributor per program studi
                </Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Certification System */}
        <Card className="ranking-card">
          <Title level={3}>
            <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: 8 }} />
            Sertifikasi AI dari Google
          </Title>
          <Alert
            message="Fitur Baru!"
            description="Sekarang Anda bisa upload sertifikat AI dari Google untuk meningkatkan kredibilitas dan skill tracking di sistem."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Paragraph>
            Sistem gamifikasi kini mendukung upload dan verifikasi sertifikasi AI dari Google.
            Fitur ini memungkinkan dosen, tendik, dan mahasiswa untuk menunjukkan kompetensi AI mereka.
          </Paragraph>

          <div style={{
            background: '#f6ffed',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
            border: '1px solid #b7eb8f'
          }}>
            <Text strong style={{ fontSize: 16 }}>📊 Sistem Poin Sertifikasi</Text>
            <div style={{ marginTop: 8 }}>
              <Text>Setiap sertifikasi AI yang diverifikasi = <Text strong style={{ color: '#14B8A6', fontSize: 16 }}>+1 poin</Text></Text>
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Poin sertifikasi akan otomatis ditambahkan ke total poin Anda dan mempengaruhi ranking di leaderboard.
              </Text>
            </div>
          </div>

          <Title level={4}>Cara Upload Sertifikasi</Title>
          <Timeline
            items={[
              {
                color: 'blue',
                children: (
                  <div>
                    <Text strong>Langkah 1:</Text> Ikuti dan selesaikan kursus AI di{' '}
                    <a href="https://www.cloudskillsboost.google" target="_blank" rel="noopener noreferrer">
                      Google Cloud Skills Boost
                    </a>,{' '}
                    <a href="https://developers.google.com/machine-learning" target="_blank" rel="noopener noreferrer">
                      Google Developers
                    </a>, Coursera, atau platform lainnya
                  </div>
                )
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 2:</Text> Dapatkan URL/link sertifikat Anda</Text>
              },
              {
                color: 'blue',
                children: (
                  <div>
                    <Text strong>Langkah 3:</Text> Upload link sertifikat melalui menu{' '}
                    <a href="/certifications/upload">"Sertifikasi AI" → "Upload Sertifikasi"</a>
                  </div>
                )
              },
              {
                color: 'blue',
                children: <Text><Text strong>Langkah 4:</Text> Tunggu verifikasi dari admin (1-3 hari kerja)</Text>
              },
              {
                color: 'green',
                children: <Text><Text strong>Langkah 5:</Text> Setelah diverifikasi, <Text strong style={{ color: '#14B8A6' }}>+1 poin</Text> otomatis ditambahkan ke leaderboard Anda! 🎉</Text>
              }
            ]}
          />

          <div style={{
            background: '#f6ffed',
            padding: 16,
            borderRadius: 8,
            marginTop: 16,
            border: '1px solid #b7eb8f'
          }}>
            <Text strong>Sertifikasi yang Didukung:</Text>

            <div style={{ marginTop: 12 }}>
              <Text strong style={{ color: '#14B8A6' }}>Untuk Mahasiswa (Students):</Text>
              <ul style={{ marginTop: 4, marginBottom: 8 }}>
                <li>Google AI Essentials for Students</li>
                <li>Introduction to Generative AI - Student</li>
              </ul>
            </div>

            <div style={{ marginTop: 8 }}>
              <Text strong style={{ color: '#14B8A6' }}>Untuk Tenaga Pendidik/Staff (Faculty):</Text>
              <ul style={{ marginTop: 4, marginBottom: 8 }}>
                <li>Google AI Essentials for Faculty</li>
                <li>Google Workspace AI for Faculty</li>
              </ul>
            </div>

            <div style={{ marginTop: 8 }}>
              <Text strong style={{ color: '#fa8c16' }}>Untuk Dosen (Educators):</Text>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                <li>Google AI Essentials for Educators</li>
                <li>Google Certified Educator</li>
                <li>Google Workspace for Education</li>
              </ul>
            </div>
          </div>

          <div style={{
            background: '#e6f7ff',
            padding: 16,
            borderRadius: 8,
            marginTop: 16,
            border: '1px solid #91d5ff'
          }}>
            <Text strong>Persyaratan File:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Format: PDF only</li>
              <li>Ukuran maksimal: 10 MB</li>
              <li>Sertifikat harus dari Google (cloudskillsboost.google atau developers.google.com)</li>
              <li>Nama Anda harus tertera jelas di sertifikat</li>
            </ul>
          </div>

          <Paragraph style={{ marginTop: 16 }}>
            <Text strong>Informasi lebih lanjut:</Text> Kunjungi halaman{' '}
            <a href="/certifications/info">Panduan Upload Sertifikasi</a> untuk instruksi lengkap.
          </Paragraph>
        </Card>

        {/* FAQ */}
        <Card className="ranking-card">
          <Title level={3}>Pertanyaan yang Sering Diajukan (FAQ)</Title>
          <Divider />

          <Title level={4}>Q: Bagaimana cara mendapatkan poin?</Title>
          <Paragraph>
            A: Buat konten edukatif di YouTube atau platform lain dengan hashtag campaign yang ditentukan.
            Setelah konten diverifikasi dan disetujui oleh admin, Anda akan mendapat poin secara otomatis.
          </Paragraph>

          <Title level={4}>Q: Apakah ranking diupdate real-time?</Title>
          <Paragraph>
            A: Ya, ranking diupdate secara otomatis setiap kali ada konten baru yang diverifikasi dan disetujui.
          </Paragraph>

          <Title level={4}>Q: Bagaimana jika dua prodi memiliki total poin yang sama?</Title>
          <Paragraph>
            A: Jika total poin sama, sistem akan memberikan rank yang sama untuk kedua prodi tersebut.
          </Paragraph>

          <Title level={4}>Q: Apakah NIM mahasiswa ditampilkan di leaderboard?</Title>
          <Paragraph>
            A: Tidak, untuk privasi mahasiswa, NIM ditampilkan dalam bentuk yang dianonimisasi (sebagian digit disamarkan).
          </Paragraph>

          <Title level={4}>Q: Dari mana data jumlah mahasiswa untuk perhitungan partisipasi?</Title>
          <Paragraph>
            A: Sistem menggunakan data resmi jumlah mahasiswa aktif per program studi. Data ini mencakup 37 program studi dengan total 18,583 mahasiswa aktif.
            Untuk perhitungan partisipasi program studi, sistem menghitung total kontribusi (video) dari semua role dibagi dengan total anggota dari semua role (dosen + mahasiswa + tendik).
          </Paragraph>

          <Title level={4}>Q: Mengapa partisipasi per role bisa lebih dari 100%?</Title>
          <Paragraph>
            A: Partisipasi per role (misalnya partisipasi dosen) bisa melebihi 100% karena satu orang bisa membuat lebih dari 1 video.
            Contoh: Jika ada 20 dosen dan mereka membuat total 50 video, partisipasi dosen = (50/20) × 100% = 250%.
            Ini berbeda dengan partisipasi program studi yang menghitung total kontribusi dibagi total anggota semua role.
          </Paragraph>

          <Divider />

          <Title level={4}>Q: Bagaimana cara mendapatkan poin dari sertifikasi?</Title>
          <Paragraph>
            A: Upload sertifikasi AI Anda melalui menu "Sertifikasi AI" → "Upload Sertifikasi". Setelah diverifikasi oleh admin, Anda akan mendapat <Text strong style={{ color: '#14B8A6' }}>+1 poin</Text> per sertifikasi yang otomatis ditambahkan ke total poin leaderboard Anda.
          </Paragraph>

          <Title level={4}>Q: Apakah poin sertifikasi berbeda dengan poin video?</Title>
          <Paragraph>
            A: Tidak, keduanya sama. Setiap video yang diverifikasi = +1 poin, setiap sertifikasi yang diverifikasi = +1 poin.
            Total poin Anda adalah gabungan dari poin video dan poin sertifikasi.
          </Paragraph>

          <Title level={4}>Q: Jenis sertifikasi apa saja yang diterima?</Title>
          <Paragraph>
            A: Semua sertifikasi AI dari platform resmi seperti Google Cloud Skills Boost, Coursera, Google Developers, dan platform lainnya.
            Yang penting adalah sertifikat berkaitan dengan AI/teknologi dan dapat diverifikasi keasliannya oleh admin.
          </Paragraph>

          <Title level={4}>Q: Berapa lama proses verifikasi sertifikasi?</Title>
          <Paragraph>
            A: Proses verifikasi biasanya memakan waktu 1-3 hari kerja. Anda dapat melihat status verifikasi di halaman "Sertifikasi Saya".
            Setelah diverifikasi, poin akan otomatis ditambahkan ke leaderboard.
          </Paragraph>
        </Card>
          </div>
        </Card>
      </div>
    </SimpleAdminLayout>
  );
};

export default RankingSystem;
